/**
 * API Client for SurrealEstateSearch backend
 */

const API_BASE = '/api';

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export type AIProvider = 'openai' | 'grok';

/**
 * Detect the browser's UI language and return a short code (e.g. "de", "fr", "it", "en").
 * Falls back to "de" for Swiss German / unknown locales.
 */
export function getBrowserLanguage(): string {
  const lang = (navigator.language || (navigator as any).userLanguage || 'de').slice(0, 2).toLowerCase();
  // Only return languages we explicitly support; default to German
  if (['de', 'fr', 'it', 'en'].includes(lang)) return lang;
  return 'de';
}

// ============================================
// Chat API
// ============================================

export async function sendChatMessage(
  messages: ChatMessage[],
  provider?: AIProvider,
  lifestyleProfile?: any
): Promise<{ message: string; isComplete: boolean; provider: AIProvider }> {
  const res = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, provider, lifestyleProfile, language: getBrowserLanguage() }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Chat failed');
  return data.data;
}

export async function extractProfile(
  messages: ChatMessage[],
  provider?: AIProvider,
  lifestylePriorities?: Record<string, number>,
  lifestyleProfile?: any,
  useLifestyle?: boolean
): Promise<{ profile: any; provider: AIProvider; searchTitle?: string }> {
  const res = await fetch(`${API_BASE}/chat/extract-profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, provider, lifestylePriorities, lifestyleProfile, useLifestyle, language: getBrowserLanguage() }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Extraction failed');
  return data.data;
}

export async function getProviders(): Promise<{ providers: AIProvider[]; default: AIProvider | null; userCanChoose?: boolean }> {
  const res = await fetch(`${API_BASE}/chat/providers`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Failed to get providers');
  return data.data;
}

export async function getLifestyleConfig(): Promise<{ enabled: boolean; factors: any[] }> {
  const res = await fetch(`${API_BASE}/chat/lifestyle-config`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Failed to get lifestyle config');
  return data.data;
}

// ============================================
// Search API
// ============================================

export async function searchListings(profile: any): Promise<{
  topMatches: any[];
  nearMisses: any[];
  stats: { total: number; matched: number; nearMiss: number; excluded: number };
  commuteOverlap?: {
    workZones: Array<{ address: string; city?: string; lat: number; lng: number; radiusKm: number; color: string }>;
    hasOverlap: boolean;
    distanceBetweenKm: number;
    maxRadiusKm: number;
    warningMessage?: string;
  } | null;
}> {
  const res = await fetch(`${API_BASE}/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profile }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Search failed');
  return data.data;
}

export async function getListingDetail(id: number): Promise<any> {
  const res = await fetch(`${API_BASE}/listings/${id}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Listing not found');
  return data.data;
}

export async function generateListingExplanation(
  listingId: number,
  listing: any,
  profile: any,
  provider?: AIProvider
): Promise<{ explanation: string; provider: AIProvider }> {
  const res = await fetch(`${API_BASE}/listings/${listingId}/explain`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ listing, profile, provider, language: getBrowserLanguage() }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Explanation generation failed');
  return data.data;
}

// ============================================
// Layers API
// ============================================

export async function getLayer(name: string): Promise<any> {
  const res = await fetch(`${API_BASE}/layers/${name}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Layer not found');
  return data.data;
}

// ============================================
// Health
// ============================================

export async function healthCheck(): Promise<any> {
  const res = await fetch(`${API_BASE}/health`);
  return res.json();
}
